import { DataNode } from 'components/WorkspaceEditor/types'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { stringToNumber } from 'shared/utils/stringUtils'

interface DisplayLevelOfSpecificationIconProps {
    node: DataNode
    isInProject: boolean
    projectLevelOfSpecification?: string | number
    isTargetLevelOfSpecificationManuallyUpdated?: boolean
    targetLevelOfSpecification?: string | number
    isContentTeamStudioView?: boolean
}

/**
 * Determine if the Level of Specification icon should be displayed
 *
 * @param params - Configuration object with node, project settings, and visibility flags
 * @returns Boolean indicating whether the icon should be displayed
 */
export const shouldDisplayLevelOfSpecificationIcon = ({
    node,
    isInProject,
    projectLevelOfSpecification,
    isTargetLevelOfSpecificationManuallyUpdated,
    targetLevelOfSpecification,
    isContentTeamStudioView = false,
}: DisplayLevelOfSpecificationIconProps): boolean => {
    const contentItem = node.contentItemView as
        | ContentItemViewNodesType
        | undefined

    // Don't show if no content item
    if (!contentItem) {
        return false
    }

    // Don't show for content team in studio view
    if (isContentTeamStudioView) {
        return false
    }

    // Only show if family is added to the project
    if (!isInProject) {
        return false
    }

    // Don't show for product types, product groups, or product-related items
    const isProductRelated =
        contentItem?.partType?.toLowerCase() === 'products' &&
        (contentItem.isProductType ||
            node.isProductGroup ||
            contentItem.isForProductType ||
            contentItem.isProductGroupHasMultipleProductTypes)

    if (isProductRelated) {
        return false
    }

    // Determine which level of specification to compare against
    const applicableLevelOfSpecification = isTargetLevelOfSpecificationManuallyUpdated
        ? targetLevelOfSpecification
        : projectLevelOfSpecification

    const itemLevelOfSpecificationTag = contentItem?.levelOfSpecificationTag

    const applicableLoS = stringToNumber(applicableLevelOfSpecification)
    const itemLoS = stringToNumber(itemLevelOfSpecificationTag)

    // Check if item's LOS tag exceeds the applicable level
    return applicableLoS !== null && itemLoS !== null && itemLoS > applicableLoS
}
